import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowRight, MapPin } from "lucide-react";
import { api } from "@/lib/api";
import PublicHeader from "@/components/PublicHeader";
import ListingCard from "@/components/ListingCard";
import Seo, { breadcrumbSchema, faqSchema, localBusinessSchema } from "@/seo/Seo";
import { CATEGORIES, GM_BOROUGHS, findBorough, findCategory, isCategoryPaused, SITE_ORIGIN } from "@/seo/data";
import { PausedCategoryNotice } from "@/components/PausedCategoryNotice";

/**
 * Category × Borough long-tail landing page. Each of the 50 combinations
 * (10 boroughs × 5 categories) is a distinct indexable URL with unique
 * H1/meta/body copy powered by /seo/data.js.
 *
 * Anti-circumvention: shows aggregated inventory count + generic listing
 * cards (which themselves respect the retailer-anonymity rule already).
 */
export default function BoroughCategoryLanding() {
  const { borough: boroughSlug, category: categorySlug } = useParams();
  const borough = findBorough(boroughSlug);
  const category = findCategory(categorySlug);
  const [listings, setListings] = useState([]);
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!borough || !category || isCategoryPaused(category.slug)) return;
    api
      .get("/listings/search", {
        params: {
          lat: borough.centroid.lat,
          lng: borough.centroid.lng,
          radius_km: 12,
          flooring_type: category.slug,
          limit: 12,
        },
      })
      .then((r) => {
        setListings(r.data.results || []);
        setCount(r.data.count || 0);
      })
      .catch(() => {
        setListings([]);
        setCount(0);
      });
  }, [borough, category]);

  const faqs = useMemo(
    () => (borough && category ? [
      {
        q: `Where can I find ${category.plural} in ${borough.name}?`,
        a: `Floor-Match aggregates surplus ${category.name.toLowerCase()} from every verified independent retailer across ${borough.name}. Enter your room dimensions (or coverage for pack products) and we'll match only the stock that fits.`,
      },
      {
        q: `Is ${category.name.toLowerCase()} from a remnant the same quality as new?`,
        a: `Yes — every ${category.name.toLowerCase()} listing on Floor-Match is either brand-new surplus (over-ordered, cancelled jobs, discontinued lines) or ex-showroom stock in as-new condition. Retailers grade condition explicitly on every listing.`,
      },
      {
        q: `Do I need to measure my room before reserving?`,
        a: category.match_mode === "dimensions"
          ? `Yes — measure width and length in metres. Our matcher works out whether each roll fits your room including 90° rotation, so a 4m × 3m room can use a 4m × 3.5m roll turned sideways.`
          : `Yes — measure the room area in metres. For ${category.name.toLowerCase()}, we add a 10 % wastage allowance by default and only show stock with enough whole packs to cover the room.`,
      },
      {
        q: `How much does ${category.name.toLowerCase()} usually cost on Floor-Match?`,
        a: `Prices are set by the retailer. Because these are surplus and remnant stock, ${borough.name} customers typically pay 30–70 % less than the same product at full retail.`,
      },
    ] : []),
    [borough, category],
  );

  if (!borough || !category) return <Navigate to="/" replace />;
  if (isCategoryPaused(category.slug)) {
    return (
      <>
        <Seo title={`${category.name} — not currently offered | FloorMatch`} description="This flooring category isn't currently offered on FloorMatch." path={`/flooring/${borough.slug}/${category.slug}`} noindex_retailer />
        <PublicHeader />
        <main className="max-w-3xl mx-auto px-4 sm:px-6 py-16"><PausedCategoryNotice name={category.name} /></main>
      </>
    );
  }

  const H1 = `${category.name} ${category.match_mode === "dimensions" ? "remnants" : "surplus"} in ${borough.name}`;
  const structured = [
    localBusinessSchema(),
    breadcrumbSchema([
      { name: "Home", url: `${SITE_ORIGIN}/` },
      { name: borough.name, url: `${SITE_ORIGIN}/flooring/${borough.slug}` },
      { name: category.name, url: `${SITE_ORIGIN}/flooring/${borough.slug}/${category.slug}` },
    ]),
    faqSchema(faqs),
    listings.length > 0
      ? {
          "@context": "https://schema.org",
          "@type": "ItemList",
          itemListElement: listings.slice(0, 10).map((l, i) => ({
            "@type": "ListItem",
            position: i + 1,
            url: `${SITE_ORIGIN}/listing/${l.id}`,
            name: l.title,
          })),
        }
      : null,
  ].filter(Boolean);

  return (
    <>
      <Seo
        title={`${category.name} ${category.match_mode === "dimensions" ? "remnants" : "surplus"} in ${borough.name}`}
        description={`${category.plural.charAt(0).toUpperCase() + category.plural.slice(1)} from verified independent flooring retailers across ${borough.name}. Matched to your room size, reserved online, collected locally. Covers ${borough.postcodes.slice(0, 4).join(", ")}.`}
        path={`/flooring/${borough.slug}/${category.slug}`}
        keywords={`${category.intent} ${borough.name.toLowerCase()}, cheap ${category.name.toLowerCase()} ${borough.name.toLowerCase()}, ${category.name.toLowerCase()} near me ${borough.name.toLowerCase()}, surplus ${category.name.toLowerCase()} ${borough.name.toLowerCase()}, ${category.plural} ${borough.name.toLowerCase()}`}
        structured_data={structured}
      />
      <PublicHeader />
      <main data-testid="borough-category-landing">
        <section className="max-w-5xl mx-auto px-4 sm:px-6 pt-10 pb-6">
          <nav className="text-xs text-slate-500 mb-3" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-slate-800">Home</Link>
            <span className="mx-1">/</span>
            <Link to={`/flooring/${borough.slug}`} className="hover:text-slate-800">{borough.name}</Link>
            <span className="mx-1">/</span>
            <span className="text-slate-700">{category.name}</span>
          </nav>
          <div className="flex items-start gap-3">
            <MapPin className="w-6 h-6 text-emerald-800 mt-1" />
            <div>
              <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl text-slate-900 tracking-tight">
                {H1}
              </h1>
              <p className="text-slate-600 mt-2 text-sm">
                {borough.tagline} — verified independent retailers holding
                surplus {category.name.toLowerCase()} stock.
              </p>
            </div>
          </div>
          <p className="text-slate-700 leading-relaxed mt-5 text-base">
            {category.intro} Every {category.name.toLowerCase()} listing you'll
            see below is held by a verified {borough.name} retailer — reserve
            online, pay online, collect in person the same week.
          </p>
        </section>

        <section className="max-w-5xl mx-auto px-4 sm:px-6 pb-10">
          <div className="flex items-baseline justify-between mb-3">
            <h2 className="font-display font-semibold text-lg text-slate-900">
              {count > 0
                ? `${count} live ${category.name.toLowerCase()} listing${count === 1 ? "" : "s"} in ${borough.name}`
                : `New ${category.name.toLowerCase()} in ${borough.name} is added as local shops list it`}
            </h2>
            <Link
              to={`/search?lat=${borough.centroid.lat}&lng=${borough.centroid.lng}&flooring_type=${category.slug}`}
              className="text-sm text-emerald-800 hover:text-emerald-900 inline-flex items-center gap-1"
              data-testid="see-all-cat-borough-link"
            >
              See all <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          {listings.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {listings.map((l) => (
                <ListingCard key={l.id} l={l} />
              ))}
            </div>
          ) : (
            <div className="text-sm text-slate-500 bg-slate-50 border border-slate-200 rounded-xl p-4">
              No live {category.name.toLowerCase()} stock in {borough.name} right
              now. Check back daily — surplus lands from independent retailers
              as jobs finish.
            </div>
          )}
        </section>

        <section className="max-w-5xl mx-auto px-4 sm:px-6 pb-10">
          <h2 className="font-display font-semibold text-lg text-slate-900 mb-3">
            Also popular in {borough.name}
          </h2>
          <ul className="flex flex-wrap gap-2">
            {CATEGORIES.filter((c) => c.slug !== category.slug).map((c) => (
              <li key={c.slug}>
                <Link
                  to={`/flooring/${borough.slug}/${c.slug}`}
                  className="inline-block bg-slate-100 text-slate-700 hover:bg-slate-200 rounded-full px-3 py-1 text-sm"
                >
                  {c.name} in {borough.name}
                </Link>
              </li>
            ))}
          </ul>
        </section>

        <section className="max-w-5xl mx-auto px-4 sm:px-6 pb-10">
          <h2 className="font-display font-semibold text-lg text-slate-900 mb-3">
            {category.name} in nearby boroughs
          </h2>
          <ul className="flex flex-wrap gap-2">
            {GM_BOROUGHS.filter((b) => b.slug !== borough.slug)
              .slice(0, 6)
              .map((b) => (
                <li key={b.slug}>
                  <Link
                    to={`/flooring/${b.slug}/${category.slug}`}
                    className="inline-block bg-slate-100 text-slate-700 hover:bg-slate-200 rounded-full px-3 py-1 text-sm"
                  >
                    {category.name} in {b.name}
                  </Link>
                </li>
              ))}
          </ul>
        </section>

        <section className="max-w-5xl mx-auto px-4 sm:px-6 pb-16" data-testid="borough-cat-faq">
          <h2 className="font-display font-semibold text-lg text-slate-900 mb-3">
            {category.name} in {borough.name} — FAQs
          </h2>
          <ul className="space-y-4">
            {faqs.map((f) => (
              <li key={f.q} className="border-b border-slate-200 pb-4">
                <h3 className="font-medium text-slate-900">{f.q}</h3>
                <p className="text-sm text-slate-700 mt-1 leading-relaxed">{f.a}</p>
              </li>
            ))}
          </ul>
        </section>
      </main>
    </>
  );
}
